import { useState, useMemo } from 'react';
import { Autocomplete, TextField, CircularProgress, Box, Stack, Typography, Chip } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useCodeSystem, usePatient } from 'src/careflow/ph-core/fhir-client/fhir-react';
import { PatientWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { produce } from 'immer';
import { getFhirIdFromEntity } from 'src/utils/fhir';
import { CodeSystemConcept } from 'src/careflow/ph-core/fhir-client/resources/resources';

const TAG_SYSTEM = 'https://terminology.patientholistics.app/fhir/CodeSystem/ph-meta-tags';
const TAG_CODE = 'Organization';

const PatientTagSelectorComponent = (props: any) => {
  const { enqueueSnackbar } = useSnackbar();
  const {
    record,
    checklistItem,
    refreshChecklistHandler,
    refreshRecordHandler,
  } = props?.others || {};

  const [searchInput, setSearchInput] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  const patientFhirId = record?.indexedFhirSubjectRefUri 
    ? getFhirIdFromEntity(record.indexedFhirSubjectRefUri)
    : null;

  const [patient, { update: updatePatient }] = usePatient(patientFhirId, {
    autofetch: !!patientFhirId,
    map: PatientWrapper,
  });

  const [organizationCodeSystem, { isFetching: isLoadingOrganizations }] = useCodeSystem('ph-organization-tags');

  const currentTags = useMemo(() => {
    if (!patient?.meta?.tag) return [];
    const tags = patient.meta.tag.filter(
      (tag) => tag.system === TAG_SYSTEM && tag.code === TAG_CODE
    );
    
    // Map tags to include proper display names from CodeSystem
    return tags.map((tag) => {
      const orgCode = tag.display;
      const org = organizationCodeSystem?.concept?.find((o: CodeSystemConcept) => o.code === orgCode);
      
      return {
        ...tag,
        display: org?.display || tag.display || 'Unknown Organization',
      };
    });
  }, [patient, organizationCodeSystem]);

  const tagOptions = useMemo(() => {
    if (!organizationCodeSystem?.concept || organizationCodeSystem.concept.length === 0) return [];
    
    return organizationCodeSystem.concept.map((concept: CodeSystemConcept) => ({
      code: concept.code || '',
      display: concept.display || concept.code || '',
      system: TAG_SYSTEM,
    }));
  }, [organizationCodeSystem]);

  const isLoadingValueSets = isLoadingOrganizations;

  const filteredOptions = useMemo(() => {
    if (!searchInput) return tagOptions;
    const searchLower = searchInput.toLowerCase();
    return tagOptions.filter(
      (option) =>
        option.display?.toLowerCase().includes(searchLower) ||
        option.code?.toLowerCase().includes(searchLower)
    );
  }, [tagOptions, searchInput]);

  const handleTagChange = async (selectedTag: any) => {
    if (!patient || !selectedTag) return;

    setIsUpdating(true);
    try {
      const updatedPatient = produce(patient, (draft) => {
        if (!draft.meta) {
          draft.meta = {};
        }
        if (!draft.meta.tag) {
          draft.meta.tag = [];
        }

        draft.meta.tag = draft.meta.tag.filter(
          (t) => !(t.system === TAG_SYSTEM && t.code === TAG_CODE)
        );

        draft.meta.tag.push({
          system: selectedTag.system || TAG_SYSTEM,
          code: TAG_CODE,
          display: selectedTag.display || selectedTag.code,
        });
      });

      await updatePatient(updatedPatient);
      enqueueSnackbar('School tag assigned successfully', { variant: 'success' });

      if (props?.others?.handler) {
        props.others.handler({
          record,
          checklistItem,
          refreshChecklistHandler,
          refreshRecordHandler,
          more: { tagAssigned: true },
        });
      }

      refreshChecklistHandler?.();
      refreshRecordHandler?.();
    } catch (error) {
      enqueueSnackbar('Failed to assign tag', { variant: 'error' });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRemoveTag = async (tagToRemove: any) => {
    if (!patient) return;

    setIsUpdating(true);
    try {
      const updatedPatient = produce(patient, (draft) => {
        if (draft.meta?.tag) {
          draft.meta.tag = draft.meta.tag.filter(
            (t) =>
              !(
                t.system === tagToRemove.system &&
                t.code === tagToRemove.code &&
                t.display === tagToRemove.display
              )
          );
          if (draft.meta.tag.length === 0) {
            delete draft.meta.tag;
          }
        }
      });

      await updatePatient(updatedPatient);
      enqueueSnackbar('Tag removed successfully', { variant: 'success' });

      if (props?.others?.handler) {
        props.others.handler({
          record,
          checklistItem,
          refreshChecklistHandler,
          refreshRecordHandler,
        });
      }

      refreshChecklistHandler?.();
      refreshRecordHandler?.();
    } catch (error) {
      enqueueSnackbar('Failed to remove tag', { variant: 'error' });
    } finally {
      setIsUpdating(false);
    }
  };

  const selectedValue = useMemo(() => {
    if (currentTags.length === 0) return null;
    const currentTag = currentTags[0];
    // The original tag.display from patient contains the org code
    // Find the matching option by code
    const orgCode = patient?.meta?.tag?.find(
      (t) => t.system === TAG_SYSTEM && t.code === TAG_CODE
    )?.display;
    
    return tagOptions.find((opt) => opt.code === orgCode) || null;
  }, [currentTags, tagOptions, patient]);

  return (
    <Box sx={{ marginBottom: 2 }}>
      {currentTags.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
          {currentTags.map((tag, index) => (
            <Chip
              key={`${tag.code}-${tag.display}-${index}`}
              label={tag.display || tag.code}
              size="small"
              onDelete={() => handleRemoveTag(tag)}
              deleteIcon={<DeleteIcon fontSize="small" />}
              sx={{ 
                backgroundColor: '#E3F2FD', 
                color: '#1976D2',
                '& .MuiChip-deleteIcon': {
                  color: '#1976D2',
                  fontSize: '16px',
                  opacity: 0.8,
                  '&:hover': {
                    color: '#d32f2f',
                    opacity: 1,
                  },
                },
              }}
            />
          ))}
        </Stack>
      )}
      <Autocomplete
        options={filteredOptions}
        getOptionLabel={(option) => option.display || option.code || ''}
        value={selectedValue}
        inputValue={searchInput}
        onInputChange={(_, newInputValue) => setSearchInput(newInputValue)}
        onChange={(_, value) => handleTagChange(value)}
        loading={isLoadingValueSets}
        disabled={isUpdating || !patientFhirId || tagOptions.length === 0}
        renderInput={(params) => (
          <TextField
            {...params}
            fullWidth
            label={`${props?.name || props?.elementName || 'School Tag'} ${checklistItem?.isRequired ? '*' : ''}`.trim()}
            placeholder={
              tagOptions.length === 0 && !isLoadingValueSets
                ? 'No organizations available'
                : 'Type to search organizations...'
            }
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoadingValueSets ? <CircularProgress size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <Box component="li" {...props}>
            <Stack>
              <Typography variant="body2">{option.display || option.code}</Typography>
              {option.code && (
                <Typography variant="caption" color="text.secondary">
                  Organization ID: {option.code}
                </Typography>
              )}
            </Stack>
          </Box>
        )}
        noOptionsText={
          isLoadingValueSets
            ? 'Loading organizations...'
            : tagOptions.length === 0
            ? 'No organizations available'
            : searchInput
            ? 'No organizations found matching your search'
            : 'Start typing to search organizations'
        }
      />
    </Box>
  );
};

export default PatientTagSelectorComponent;

